import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom'
import {
  PlusIcon,
  BuildingIcon,
  MapPinIcon,
  FileTextIcon,
  ClipboardIcon,
  ClipboardListIcon,
  UsersIcon,
  SaveIcon,
  CreditCardIcon,
  StickyNoteIcon,
  HistoryIcon,
  BriefcaseIcon,
  EditIcon,
  MoreHorizontalIcon,
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import { getEntityActivities, ActivityLog, logActivity, ActivityTypes } from '../utils/activityLogger'
import { parseEntityNotes } from '../lib/entityNotes'
import { NotesAndActivityPanel } from '../components/NotesAndActivityPanel'
import { getContactDisplayName } from '../lib/leadHelpers'
import { ConversationsPanel } from '../components/ConversationsPanel'
import { ViewTabBar } from '../components/ViewPageLayout'
import {
  formatPropertyPaymentMethod,
  formatPropertySalesTaxStatus,
} from '../components/property/types'
import { getInvoiceDisplayStatus, getInvoiceStatusColor, getInvoiceStatusLabel } from '../lib/invoiceStatus'
import { downloadStatementPdf, buildStatementPdfBlob } from '../lib/downloadStatementPdf'
import { sendEmail, blobToBase64 } from '../lib/sendEmail'
import { MobilePage, MobilePageHeader, MobileSheet, mobile } from '../components/mobile'

interface CustomerNote {
  timestamp: string
  note: string
}

interface Property {
  id: string
  name: string
  address: string
  city: string
  state: string
  zip_code: string
  property_type: string
  building_type?: string
  sqft: number
  notes: string
  payment_method?: string | null
  sales_tax_status?: string | null
}

export default function CustomerView() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('returnTo')
  const tabFromUrl = searchParams.get('tab') as 'overview' | 'contacts' | 'properties' | 'contracts' | 'jobs' | 'proposals' | 'invoices' | 'notes' | null
  const contactIdFromUrl = searchParams.get('contact')
  const [activeTab, setActiveTab] = useState<
    'overview' | 'contacts' | 'properties' | 'contracts' | 'jobs' | 'proposals' | 'invoices' | 'notes'
  >(tabFromUrl || 'overview')

  const [customer, setCustomer] = useState<any>(null)
  const [properties, setProperties] = useState<Property[]>([])
  const [notes, setNotes] = useState<CustomerNote[]>([])
  const [contacts, setContacts] = useState<any[]>([])
  const [proposals, setProposals] = useState<any[]>([])
  const [proposalRequests, setProposalRequests] = useState<any[]>([])
  const [contracts, setContracts] = useState<any[]>([])
  const [jobs, setJobs] = useState<any[]>([])
  const [invoices, setInvoices] = useState<any[]>([])
  const [payments, setPayments] = useState<any[]>([])
  const [statementBusy, setStatementBusy] = useState<'download' | 'email' | null>(null)
  const [loading, setLoading] = useState(true)
  const [newNote, setNewNote] = useState('')
  const [savingNote, setSavingNote] = useState(false)
  const [activities, setActivities] = useState<ActivityLog[]>([])
  const [highlightedContactId, setHighlightedContactId] = useState<string | null>(contactIdFromUrl)
  const [actionsOpen, setActionsOpen] = useState(false)

  const primaryContact = contacts.length > 0 ? contacts[0] : null
  const primaryContactName =
    primaryContact != null
      ? getContactDisplayName(primaryContact)
      : [customer?.contact_first_name, customer?.contact_last_name].filter(Boolean).join(' ') || 'N/A'
  const primaryContactLinkId =
    primaryContact?.id != null ? String(primaryContact.id) : primaryContact ? '0' : null

  useEffect(() => {
    if (id) {
      fetchCustomerData()
      fetchActivities()
      fetchProposals()
      fetchProposalRequests()
      fetchContracts()
      fetchJobs()
      fetchInvoices()
      fetchPayments()
    }
  }, [id])

  // Set active tab and highlighted contact from URL params
  useEffect(() => {
    if (tabFromUrl) {
      setActiveTab(tabFromUrl)
    }
    if (contactIdFromUrl) {
      setHighlightedContactId(contactIdFromUrl)
    }
  }, [tabFromUrl, contactIdFromUrl])

  useEffect(() => {
    if (activeTab !== 'contacts' || !highlightedContactId) return
    const el = document.getElementById(`customer-contact-${highlightedContactId}`)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [activeTab, highlightedContactId, contacts])

  useEffect(() => {
    if (activeTab === 'notes' && id) {
      fetchActivities()
    }
  }, [activeTab, id])

  const fetchActivities = async () => {
    if (!id) return
    try {
      const acts = await getEntityActivities('customer', id)
      setActivities(acts)
    } catch (error) {
      console.error('Error fetching activities:', error)
    }
  }

  const fetchProposals = async () => {
    if (!id) return
    try {
      // Fetch proposals linked to this customer (only actual proposals, not requests)
      const { data, error } = await supabase
        .from('proposals')
        .select('*')
        .eq('customer_id', id)
        .eq('record_type', 'proposal')
        .order('created_at', { ascending: false })

      if (error) throw error

      console.log('Fetched proposals for customer:', id, data)

      // If we have proposals with property_id, fetch property details separately
      if (data && data.length > 0) {
        const propertyIds = data.filter(p => p.property_id).map(p => p.property_id)
        if (propertyIds.length > 0) {
          const { data: propertiesData } = await supabase
            .from('properties')
            .select('id, name, address')
            .in('id', propertyIds)

          // Merge property data into proposals
          const proposalsWithProperties = data.map(proposal => ({
            ...proposal,
            property: propertiesData?.find(prop => prop.id === proposal.property_id) || null
          }))
          setProposals(proposalsWithProperties)
          return
        }
      }

      setProposals(data || [])
    } catch (error) {
      console.error('Error fetching proposals:', error)
    }
  }

  const fetchProposalRequests = async () => {
    if (!id) return
    try {
      const { data, error } = await supabase
        .from('proposals')
        .select('*')
        .eq('customer_id', id)
        .eq('record_type', 'request')
        .order('created_at', { ascending: false })

      if (error) throw error

      // If we have requests with property_id, fetch property details separately
      if (data && data.length > 0) {
        const propertyIds = data.filter(p => p.property_id).map(p => p.property_id)
        if (propertyIds.length > 0) {
          const { data: propertiesData } = await supabase
            .from('properties')
            .select('id, name, address')
            .in('id', propertyIds)

          // Merge property data into requests
          const requestsWithProperties = data.map(request => ({
            ...request,
            property: propertiesData?.find(prop => prop.id === request.property_id) || null
          }))
          setProposalRequests(requestsWithProperties)
          return
        }
      }
      setProposalRequests(data || [])
    } catch (error) {
      console.error('Error fetching proposal requests:', error)
    }
  }


  const fetchContracts = async () => {
    if (!id) return
    try {
      // Fetch contracts linked to this customer
      const { data, error } = await supabase
        .from('contracts')
        .select('*')
        .eq('customer_id', id)
        .order('created_at', { ascending: false })

      if (error) throw error

      console.log('Fetched contracts for customer:', id, data)
      setContracts(data || [])
    } catch (error) {
      console.error('Error fetching contracts:', error)
    }
  }

  const fetchJobs = async () => {
    if (!id) return
    try {
      const { data, error } = await supabase
        .from('jobs')
        .select('*')
        .eq('customer_id', id)
        .order('scheduled_date', { ascending: false })

      if (error) throw error

      if (data && data.length > 0) {
        const propertyIds = data.filter(j => j.property_id).map(j => j.property_id)
        if (propertyIds.length > 0) {
          const { data: propertiesData } = await supabase
            .from('properties')
            .select('id, name, address')
            .in('id', propertyIds)

          const jobsWithProperties = data.map(job => ({
            ...job,
            property: propertiesData?.find(prop => prop.id === job.property_id) || null
          }))
          setJobs(jobsWithProperties)
          return
        }
      }
      setJobs(data || [])
    } catch (error) {
      console.error('Error fetching jobs:', error)
    }
  }

  const fetchInvoices = async () => {
    if (!id) return
    try {
      const { data, error } = await supabase
        .from('invoices')
        .select('*')
        .eq('customer_id', id)
        .order('created_at', { ascending: false })

      if (error) throw error
      setInvoices(data || [])
    } catch (error) {
      console.error('Error fetching invoices:', error)
    }
  }

  const fetchPayments = async () => {
    if (!id) return
    try {
      const { data, error } = await supabase
        .from('payments')
        .select('*, invoice:invoices(invoice_number)')
        .eq('customer_id', id)
        .is('voided_at', null)
        .order('payment_date', { ascending: false })

      if (error) throw error
      setPayments(data || [])
    } catch (error) {
      console.error('Error fetching payments:', error)
    }
  }

  const buildStatementProps = () => {
    const openInvoices = invoices
      .filter((inv) => Number(inv.balance_due ?? 0) > 0 && inv.status !== 'draft')
      .map((inv) => {
        const total = Number(inv.total || inv.total_amount || inv.subtotal || 0)
        const amountPaid = Number(inv.amount_paid ?? 0)
        return {
          invoiceNumber: inv.invoice_number || 'N/A',
          invoiceDate: inv.invoice_date || inv.issue_date,
          dueDate: inv.due_date,
          total,
          amountPaid,
          balanceDue: Number(inv.balance_due ?? (total - amountPaid)),
        }
      })
    const paymentRows = payments.map((p) => ({
      paymentDate: p.payment_date,
      invoiceNumber: p.invoice?.invoice_number || 'N/A',
      method: p.payment_method || 'other',
      amount: Number(p.amount || 0),
    }))
    return {
      customerName: customer?.company_name || 'Customer',
      asOfDate: new Date().toISOString(),
      invoices: openInvoices,
      payments: paymentRows,
      totalBalanceDue: openInvoices.reduce((sum, inv) => sum + inv.balanceDue, 0),
    }
  }

  const handleDownloadStatement = async () => {
    try {
      setStatementBusy('download')
      const props = buildStatementProps()
      await downloadStatementPdf(props, `Statement-${customer?.company_name || id}`)
    } catch (err: any) {
      alert('Failed to generate statement: ' + (err.message || 'Unknown error'))
    } finally {
      setStatementBusy(null)
    }
  }

  const handleEmailStatement = async () => {
    const to = customer?.email
    if (!to) {
      alert('This customer has no email address on file.')
      return
    }
    try {
      setStatementBusy('email')
      const props = buildStatementProps()
      const blob = await buildStatementPdfBlob(props)
      const base64 = await blobToBase64(blob)
      await sendEmail({
        to,
        subject: 'Account Statement from RBC Facility Services',
        text: `Please find attached your account statement. Total balance due: $${props.totalBalanceDue.toFixed(2)}.`,
        attachments: [
          { filename: 'Statement.pdf', content: base64, contentType: 'application/pdf' },
        ],
      })
      await logActivity({
        activity_type: ActivityTypes.STATEMENT_EMAILED,
        entity_type: 'customer',
        entity_id: id || '',
        description: `Account statement emailed to ${to}`,
      })
      alert('Statement emailed successfully.')
    } catch (err: any) {
      alert('Failed to email statement: ' + (err.message || 'Unknown error'))
    } finally {
      setStatementBusy(null)
    }
  }

  const fetchCustomerData = async () => {
    try {
      // Fetch customer
      const { data: customerData, error: customerError } = await supabase
        .from('customers')
        .select('*')
        .eq('id', id)
        .single()

      if (customerError) throw customerError
      setCustomer(customerData)

      // Fetch notes if available
      setNotes(parseEntityNotes(customerData.notes))

      // Fetch contacts if available
      if (customerData.contacts && Array.isArray(customerData.contacts)) {
        setContacts(customerData.contacts)
      }

      // Fetch properties
      const { data: propertiesData, error: propertiesError } = await supabase
        .from('properties')
        .select('*')
        .eq('customer_id', id)

      if (!propertiesError && propertiesData) {
        setProperties(propertiesData)
      }
    } catch (error) {
      console.error('Error fetching customer data:', error)
      alert('Error loading customer: ' + (error as any).message)
    } finally {
      setLoading(false)
    }
  }

  const handleAddNote = async () => {
    if (!newNote.trim() || !id) return

    setSavingNote(true)
    try {
      const noteToAdd: CustomerNote = {
        timestamp: new Date().toISOString(),
        note: newNote.trim(),
      }

      const updatedNotes = [...notes, noteToAdd]

      const { error } = await supabase
        .from('customers')
        .update({ notes: updatedNotes })
        .eq('id', id)

      if (error) throw error

      setNotes(updatedNotes)
      setNewNote('')

      await logActivity({
        activity_type: 'note_added',
        entity_type: 'customer',
        entity_id: id,
        description: 'Customer note added',
      })
      fetchActivities()
    } catch (error) {
      console.error('Error adding note:', error)
      alert('Error adding note')
    } finally {
      setSavingNote(false)
    }
  }

  if (loading) {
    return (
      <MobilePage className="flex items-center justify-center min-h-[400px]">
        <p className="text-gray-600">Loading...</p>
      </MobilePage>
    )
  }

  if (!customer) {
    return (
      <MobilePage className="flex items-center justify-center min-h-[400px]">
        <p className="text-gray-600">Customer not found</p>
      </MobilePage>
    )
  }

  const handleBack = () => {
    if (returnTo) {
      navigate(decodeURIComponent(returnTo))
    } else {
      navigate('/customers')
    }
  }
  const backLabel = returnTo
    ? (() => {
        const decoded = decodeURIComponent(returnTo)
        if (decoded.includes('/proposals/view')) return 'Back to Proposal'
        if (decoded.includes('/contracts/view') || decoded.includes('/contracts/details'))
          return 'Back to Contract'
        if (decoded.includes('/jobs/details')) return 'Back to Job'
        if (decoded.includes('/invoices/')) return 'Back to Invoice'
        return 'Back'
      })()
    : 'Back to Customers'

  const handleStatusChange = async (newStatus: 'onboarding' | 'active' | 'terminated') => {
    try {
      const { error } = await supabase.from('customers').update({ status: newStatus }).eq('id', id)
      if (error) throw error
      setCustomer({ ...customer, status: newStatus })
      await logActivity({
        activity_type: 'status_changed',
        entity_type: 'customer',
        entity_id: id!,
        description: `Customer status changed to ${newStatus}`,
        metadata: { status: newStatus },
      })
      fetchActivities()
    } catch (error) {
      console.error('Error updating customer status:', error)
      alert('Error updating customer status: ' + (error as any).message)
    }
  }

  return (
    <MobilePage className="max-w-6xl mx-auto pb-28 lg:pb-8">
      <MobilePageHeader
        sticky
        title={customer.company_name || 'Customer'}
        subtitle={
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-0.5">
            {customer.email && <span className="truncate max-w-[12rem] sm:max-w-none">{customer.email}</span>}
            {customer.phone && <span>{customer.phone}</span>}
            <select
              value={customer.status || (customer.is_active ? 'active' : 'terminated')}
              onChange={(e) =>
                handleStatusChange(e.target.value as 'onboarding' | 'active' | 'terminated')
              }
              className={`text-xs font-semibold rounded-full px-3 py-1 border-0 cursor-pointer focus:ring-2 focus:ring-blue-500 focus:outline-none ${
                customer.status === 'active' || (!customer.status && customer.is_active)
                  ? 'bg-green-100 text-green-800 hover:bg-green-200'
                  : customer.status === 'onboarding'
                    ? 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                    : 'bg-red-100 text-red-800 hover:bg-red-200'
              }`}
            >
              <option value="onboarding">Onboarding</option>
              <option value="active">Active</option>
              <option value="terminated">Terminated</option>
            </select>
          </div>
        }
        onBack={handleBack}
        backLabel={backLabel}
        actions={
          <button
            type="button"
            onClick={() => setActionsOpen(true)}
            className={`${mobile.secondaryBtn} !min-h-[40px] !px-3 text-xs sm:text-sm`}
            aria-label="More actions"
          >
            <MoreHorizontalIcon className="w-4 h-4" />
            <span className="hidden sm:inline">Actions</span>
          </button>
        }
      />

      <div className={`${mobile.surface} flex flex-col min-h-0`}>
        <ViewTabBar
          sticky
          tabs={[
            { id: 'overview', label: 'Overview' },
            { id: 'contacts', label: 'Contacts' },
            { id: 'properties', label: 'Properties' },
            { id: 'contracts', label: 'Contracts' },
            { id: 'jobs', label: 'Jobs' },
            { id: 'proposals', label: 'Proposals' },
            { id: 'invoices', label: 'Invoices' },
            { id: 'notes', label: 'Notes', full: 'Notes & Activity' },
          ]}
          activeTab={activeTab}
          onChange={setActiveTab}
        />

          <div className="p-3 sm:p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <MapPinIcon className="w-5 h-5 text-purple-600" />
                      <h3 className="font-semibold text-gray-900">Properties</h3>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">{properties.length}</p>
                  </div>
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <ClipboardIcon className="w-5 h-5 text-green-600" />
                      <h3 className="font-semibold text-gray-900">Contracts</h3>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">{contracts.length}</p>
                  </div>
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <FileTextIcon className="w-5 h-5 text-orange-600" />
                      <h3 className="font-semibold text-gray-900">Jobs</h3>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">{jobs.length}</p>
                  </div>
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <CreditCardIcon className="w-5 h-5 text-blue-600" />
                      <h3 className="font-semibold text-gray-900">Invoices</h3>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">{invoices.length}</p>
                  </div>
                </div>

                {id && <ConversationsPanel entityType="customer" entityId={id} />}

                <div className="bg-blue-50 rounded-lg p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <BuildingIcon className="w-6 h-6 text-blue-600" />
                    <h2 className="text-xl font-semibold text-gray-900">Company Information</h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Company Name
                      </label>
                      <p className="mt-1 text-sm text-gray-900">{customer.company_name || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Email</label>
                      <p className="mt-1 text-sm text-gray-900">{customer.email || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Phone</label>
                      <p className="mt-1 text-sm text-gray-900">{customer.phone || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Address</label>
                      <p className="mt-1 text-sm text-gray-900">
                        {customer.address || 'N/A'}
                        {customer.city && `, ${customer.city}`}
                        {customer.state && `, ${customer.state}`}
                        {customer.zip_code && ` ${customer.zip_code}`}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-teal-50 rounded-lg p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <ClipboardListIcon className="w-6 h-6 text-teal-600" />
                    <h2 className="text-xl font-semibold text-gray-900">Customer Management</h2>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="bg-white rounded-lg border border-teal-100 p-4 shadow-sm">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                      <select
                        value={customer.status || (customer.is_active ? 'active' : 'terminated')}
                        onChange={async (e) => {
                          const newStatus = e.target.value as 'onboarding' | 'active' | 'terminated'
                          try {
                            const { error } = await supabase
                              .from('customers')
                              .update({ status: newStatus })
                              .eq('id', id)

                            if (error) throw error

                            setCustomer({ ...customer, status: newStatus })

                            await logActivity({
                              activity_type: 'status_changed',
                              entity_type: 'customer',
                              entity_id: id!,
                              description: `Customer status changed to ${newStatus}`,
                              metadata: { status: newStatus },
                            })

                            fetchActivities()
                          } catch (error) {
                            console.error('Error updating customer status:', error)
                            alert('Error updating customer status: ' + (error as any).message)
                          }
                        }}
                        className={`text-xs font-semibold rounded-full px-3 py-2 border-0 cursor-pointer focus:ring-2 focus:ring-teal-500 focus:outline-none w-full ${
                          customer.status === 'active' || (!customer.status && customer.is_active)
                            ? 'bg-green-100 text-green-800 hover:bg-green-200'
                            : customer.status === 'onboarding'
                            ? 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                            : 'bg-red-100 text-red-800 hover:bg-red-200'
                        }`}
                      >
                        <option value="onboarding">Onboarding</option>
                        <option value="active">Active</option>
                        <option value="terminated">Terminated</option>
                      </select>
                    </div>
                    <div className="bg-white rounded-lg border border-teal-100 p-4 shadow-sm">
                      <label className="block text-sm font-medium text-gray-700">Primary Contact</label>
                      {primaryContactLinkId && primaryContactName !== 'N/A' ? (
                        <Link
                          to={`/customers/view/${id}?tab=contacts&contact=${primaryContactLinkId}`}
                          className="mt-1 inline-block text-sm font-medium text-blue-600 hover:text-blue-800 underline"
                        >
                          {primaryContactName}
                        </Link>
                      ) : (
                        <p className="mt-1 text-sm font-medium text-gray-900">{primaryContactName}</p>
                      )}
                    </div>
                    <div className="bg-white rounded-lg border border-teal-100 p-4 shadow-sm">
                      <label className="block text-sm font-medium text-gray-700">Contacts</label>
                      <p className="mt-1 text-sm font-medium text-gray-900">{contacts.length}</p>
                    </div>
                    <div className="bg-white rounded-lg border border-teal-100 p-4 shadow-sm">
                      <label className="block text-sm font-medium text-gray-700">
                        Default Payment Method
                      </label>
                      <p className="mt-1 text-sm font-medium text-gray-900">
                        {customer.payment_method || 'Not set'}
                      </p>
                    </div>
                    <div className="bg-white rounded-lg border border-teal-100 p-4 shadow-sm">
                      <label className="block text-sm font-medium text-gray-700">
                        Default Sales Tax Status
                      </label>
                      <p className="mt-1 text-sm font-medium text-gray-900">
                        {customer.sales_tax_status || 'Not set'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Contacts Tab */}
            {activeTab === 'contacts' && (
              <div className="space-y-6">
                <div className="bg-green-50 rounded-lg p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <UsersIcon className="w-6 h-6 text-green-600" />
                    <h2 className="text-xl font-semibold text-gray-900">
                      Contacts ({contacts.length})
                    </h2>
                  </div>
                  {contacts.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {contacts.map((contact, index) => {
                        const contactKey = String(contact.id ?? index)
                        return (
                        <div
                          key={contact.id || index}
                          id={`customer-contact-${contactKey}`}
                          className={`bg-white rounded-lg border p-4 shadow-sm transition-shadow ${
                            highlightedContactId === contactKey
                              ? 'border-blue-500 ring-2 ring-blue-200'
                              : 'border-green-100'
                          }`}
                        >
                          <h3 className="font-semibold text-gray-900 mb-1">{getContactDisplayName(contact)}</h3>
                          {contact.title && (
                            <p className="text-sm text-gray-600 mb-3">{contact.title}</p>
                          )}
                          <div className="space-y-2 text-sm">
                            {contact.phone && (
                              <div>
                                <label className="block text-xs font-medium text-gray-500">Phone</label>
                                <a
                                  href={`tel:${contact.phone.replace(/\D/g, '')}`}
                                  className="text-blue-600 hover:text-blue-800 underline"
                                >
                                  {contact.phone}
                                </a>
                              </div>
                            )}
                            {contact.cell && (
                              <div>
                                <label className="block text-xs font-medium text-gray-500">Cell</label>
                                <a
                                  href={`tel:${contact.cell.replace(/\D/g, '')}`}
                                  className="text-blue-600 hover:text-blue-800 underline"
                                >
                                  {contact.cell}
                                </a>
                              </div>
                            )}
                            {contact.email && (
                              <div>
                                <label className="block text-xs font-medium text-gray-500">Email</label>
                                <a
                                  href={`mailto:${contact.email}`}
                                  className="text-blue-600 hover:text-blue-800 underline break-all"
                                >
                                  {contact.email}
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                        )
                      })}
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg border border-dashed border-green-200 p-8 text-center">
                      <UsersIcon className="w-10 h-10 text-green-300 mx-auto mb-3" />
                      <p className="text-gray-500">No contacts found</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Properties Tab */}
            {activeTab === 'properties' && (
              <div className="space-y-6">
                <div className="bg-purple-50 rounded-lg p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <MapPinIcon className="w-6 h-6 text-purple-600" />
                    <h2 className="text-xl font-semibold text-gray-900">
                      Properties ({properties.length})
                    </h2>
                  </div>
                  {properties.length > 0 ? (
                    <div className="space-y-4">
                      {properties.map((property) => (
                        <div
                          key={property.id}
                          className="bg-white rounded-lg border border-purple-100 p-5 shadow-sm"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <h3 className="text-lg font-semibold text-gray-900">{property.name}</h3>
                              <p className="text-sm text-gray-600 mt-1">
                                {property.address} {property.city ? property.city + ', ' : ''}
                                {property.state} {property.zip_code}
                              </p>
                              <div className="mt-3 flex flex-wrap gap-4 text-sm text-gray-600">
                                <span>Type: {property.property_type || property.building_type || 'N/A'}</span>
                                {property.sqft && <span>Sqft: {property.sqft.toLocaleString()}</span>}
                                <span>
                                  Payment:{' '}
                                  {formatPropertyPaymentMethod(property.payment_method, customer)}
                                </span>
                                <span>
                                  Tax:{' '}
                                  {formatPropertySalesTaxStatus(property.sales_tax_status, customer)}
                                </span>
                              </div>
                            </div>
                            <button
                              onClick={() => navigate(`/properties/view/${property.id}`)}
                              className="text-sm text-purple-700 hover:text-purple-900 font-medium shrink-0"
                            >
                              View
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg border border-dashed border-purple-200 p-8 text-center">
                      <MapPinIcon className="w-10 h-10 text-purple-300 mx-auto mb-3" />
                      <p className="text-gray-500">No properties found</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Contracts Tab */}
            {activeTab === 'contracts' && (
              <div className="space-y-6">
                <div className="bg-emerald-50 rounded-lg p-6">
                  <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
                    <div className="flex items-center gap-2">
                      <ClipboardIcon className="w-6 h-6 text-emerald-600" />
                      <h2 className="text-xl font-semibold text-gray-900">
                        Contracts ({contracts.length})
                      </h2>
                    </div>
                    <button
                      onClick={() => {
                        const returnTo = encodeURIComponent(`/customers/view/${id}?tab=contracts`)
                        navigate(`/contracts/create?customerId=${id}&returnTo=${returnTo}`)
                      }}
                      className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                    >
                      <PlusIcon className="w-4 h-4" />
                      New Contract
                    </button>
                  </div>

                  {contracts.length === 0 ? (
                    <div className="bg-white rounded-lg border border-dashed border-emerald-200 p-8 text-center">
                      <ClipboardIcon className="w-10 h-10 text-emerald-300 mx-auto mb-3" />
                      <p className="text-gray-500">No contracts found</p>
                    </div>
                  ) : (
                    <>
                    <ul className="space-y-2 lg:hidden">
                      {contracts.map((contract) => (
                        <li key={contract.id} className="rounded-xl border border-emerald-100 bg-white p-3 space-y-1.5">
                          <button
                            type="button"
                            onClick={() => {
                              const returnTo = encodeURIComponent(`/customers/view/${id}?tab=contracts`)
                              navigate(`/contracts/details/${contract.id}?returnTo=${returnTo}`)
                            }}
                            className="text-sm font-semibold text-blue-600 text-left"
                          >
                            {contract.title || 'Untitled Contract'}
                          </button>
                          <p className="text-xs text-gray-500">{contract.contract_number || 'N/A'}</p>
                          <p className="text-sm text-gray-900">
                            ${(contract.total_amount || 0).toFixed(2)} · {contract.status || 'draft'}
                          </p>
                        </li>
                      ))}
                    </ul>
                    <div className="hidden lg:block overflow-x-auto bg-white rounded-lg border border-emerald-100 shadow-sm">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-emerald-50/80">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Contract Number
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Title
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Type
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Amount
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {contracts.map((contract) => (
                          <tr key={contract.id} className="hover:bg-emerald-50/30">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {contract.contract_number || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {contract.title || 'Untitled Contract'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {contract.contract_type === 'recurring'
                                ? 'Recurring'
                                : contract.contract_type === 'as_needed'
                                  ? 'Ongoing (As Needed)'
                                  : 'One-Time'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  contract.status === 'active'
                                    ? 'bg-green-100 text-green-800'
                                    : contract.status === 'draft'
                                    ? 'bg-gray-100 text-gray-800'
                                    : contract.status === 'pending_signature'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : contract.status === 'cancelled'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}
                              >
                                {contract.status || 'draft'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              ${(contract.total_amount || 0).toFixed(2)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <button
                                onClick={() => {
                                  const returnTo = encodeURIComponent(`/customers/view/${id}?tab=contracts`)
                                  navigate(`/contracts/details/${contract.id}?returnTo=${returnTo}`)
                                }}
                                className="text-blue-600 hover:text-blue-900 mr-4"
                              >
                                View
                              </button>
                              <button
                                onClick={() => {
                                  const returnTo = encodeURIComponent(`/customers/view/${id}?tab=contracts`)
                                  navigate(`/contracts/edit/${contract.id}?returnTo=${returnTo}`)
                                }}
                                className="text-indigo-600 hover:text-indigo-900"
                              >
                                Edit
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                    </>
                )}
                </div>
              </div>
            )}

            {/* Jobs Tab */}
            {activeTab === 'jobs' && (
              <div className="space-y-6">
                <div className="bg-sky-50 rounded-lg p-6">
                  <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
                    <div className="flex items-center gap-2">
                      <BriefcaseIcon className="w-6 h-6 text-sky-600" />
                      <h2 className="text-xl font-semibold text-gray-900">Jobs ({jobs.length})</h2>
                    </div>
                    <button
                      onClick={() => {
                        const returnTo = encodeURIComponent(`/customers/view/${id}?tab=jobs`)
                        navigate(`/jobs/create?customer_id=${id}&returnTo=${returnTo}`)
                      }}
                      className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                    >
                      <PlusIcon className="w-4 h-4" />
                      New Job
                    </button>
                  </div>

                  {jobs.length === 0 ? (
                    <div className="bg-white rounded-lg border border-dashed border-sky-200 p-8 text-center">
                      <BriefcaseIcon className="w-10 h-10 text-sky-300 mx-auto mb-3" />
                      <p className="text-gray-500">No jobs found</p>
                    </div>
                  ) : (
                    <>
                    <ul className="space-y-2 lg:hidden">
                      {jobs.map((job) => (
                        <li key={job.id} className="rounded-xl border border-sky-100 bg-white p-3 space-y-1.5">
                          <button
                            type="button"
                            onClick={() => {
                              const returnTo = encodeURIComponent(`/customers/view/${id}?tab=jobs`)
                              navigate(`/jobs/details/${job.id}?returnTo=${returnTo}`)
                            }}
                            className="text-sm font-semibold text-blue-600 text-left"
                          >
                            {job.title || 'Untitled Job'}
                          </button>
                          <p className="text-xs text-gray-500">{job.job_number || 'N/A'}</p>
                          <p className="text-sm text-gray-900">
                            {job.status || 'N/A'}
                            {job.scheduled_date ? ` · ${new Date(job.scheduled_date).toLocaleDateString()}` : ''}
                          </p>
                        </li>
                      ))}
                    </ul>
                    <div className="hidden lg:block overflow-x-auto bg-white rounded-lg border border-sky-100 shadow-sm">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-sky-50/80">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Job Number
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Title
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Scheduled Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Property
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {jobs.map((job) => (
                          <tr key={job.id} className="hover:bg-sky-50/30">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {job.job_number || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {job.title || 'Untitled Job'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  job.status === 'completed'
                                    ? 'bg-green-100 text-green-800'
                                    : job.status === 'in_progress'
                                    ? 'bg-blue-100 text-blue-800'
                                    : job.status === 'scheduled'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : job.status === 'cancelled'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}
                              >
                                {job.status || 'draft'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {job.scheduled_date
                                ? new Date(job.scheduled_date).toLocaleDateString()
                                : 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {job.property?.name || job.property?.address || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right">
                              <button
                                onClick={() => {
                                  const returnTo = encodeURIComponent(`/customers/view/${id}?tab=jobs`)
                                  navigate(`/jobs/details/${job.id}?returnTo=${returnTo}`)
                                }}
                                className="text-blue-600 hover:text-blue-900 mr-4"
                              >
                                View
                              </button>
                              <button
                                onClick={() => {
                                  const returnTo = encodeURIComponent(`/customers/view/${id}?tab=jobs`)
                                  navigate(`/jobs/edit/${job.id}?returnTo=${returnTo}`)
                                }}
                                className="text-indigo-600 hover:text-indigo-900"
                              >
                                Edit
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                    </>
                )}
                </div>
              </div>
            )}

            {/* Proposals Tab */}
            {activeTab === 'proposals' && (
              <div className="space-y-6">
                {/* Proposals Section */}
                <div className="bg-orange-50 rounded-lg p-6">
                  <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
                    <div className="flex items-center gap-2">
                      <FileTextIcon className="w-6 h-6 text-orange-600" />
                      <h2 className="text-xl font-semibold text-gray-900">
                        Proposals ({proposals.length})
                      </h2>
                    </div>
                    <button
                      onClick={() => {
                        const returnTo = encodeURIComponent(`/customers/view/${id}?tab=proposals`)
                        navigate(`/proposals/create?customer=${id}&returnTo=${returnTo}`)
                      }}
                      className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                    >
                      <PlusIcon className="w-4 h-4" />
                      New Proposal
                    </button>
                  </div>

                  {proposals.length === 0 ? (
                    <div className="bg-white rounded-lg border border-dashed border-orange-200 p-8 text-center">
                      <FileTextIcon className="w-10 h-10 text-orange-300 mx-auto mb-3" />
                      <p className="text-gray-500">No proposals found</p>
                    </div>
                  ) : (
                    <>
                    <ul className="space-y-2 lg:hidden">
                      {proposals.map((proposal) => (
                        <li key={proposal.id} className="rounded-xl border border-orange-100 bg-white p-3 space-y-1.5">
                          <button
                            type="button"
                            onClick={() => {
                              const returnTo = encodeURIComponent(`/customers/view/${id}?tab=proposals`)
                              navigate(`/proposals/view/${proposal.id}?returnTo=${returnTo}`)
                            }}
                            className="text-sm font-semibold text-blue-600 text-left"
                          >
                            {proposal.title || 'Untitled'}
                          </button>
                          <p className="text-xs text-gray-500">{proposal.proposal_number}</p>
                          <p className="text-sm text-gray-900">
                            ${(proposal.total || proposal.total_amount || 0).toLocaleString()} · {proposal.status || 'draft'}
                          </p>
                        </li>
                      ))}
                    </ul>
                    <div className="hidden lg:block overflow-x-auto bg-white rounded-lg border border-orange-100 shadow-sm">
                      <table className="w-full">
                        <thead className="bg-orange-50/80">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Title
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Property
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Amount
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Status
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Date
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {proposals.map((proposal) => (
                            <tr key={proposal.id} className="hover:bg-orange-50/30">
                              <td className="px-4 py-3">
                                <button
                                  onClick={() => {
                                    const returnTo = encodeURIComponent(`/customers/view/${id}?tab=proposals`)
                                    navigate(`/proposals/view/${proposal.id}?returnTo=${returnTo}`)
                                  }}
                                  className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                                >
                                  {proposal.title || 'Untitled'}
                                </button>
                                <div className="text-xs text-gray-500">{proposal.proposal_number}</div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {proposal.property?.name || proposal.property?.address || 'N/A'}
                              </td>
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                ${(proposal.total || 0).toLocaleString()}
                              </td>
                              <td className="px-4 py-3">
                                <span
                                  className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                    proposal.status === 'approved'
                                      ? 'bg-green-100 text-green-800'
                                      : proposal.status === 'sent'
                                      ? 'bg-blue-100 text-blue-800'
                                      : proposal.status === 'rejected'
                                      ? 'bg-red-100 text-red-800'
                                      : proposal.status === 'expired'
                                      ? 'bg-gray-100 text-gray-800'
                                      : 'bg-yellow-100 text-yellow-800'
                                  }`}
                                >
                                  {proposal.status?.charAt(0).toUpperCase() + proposal.status?.slice(1)}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {new Date(proposal.created_at).toLocaleDateString()}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => {
                                      const returnTo = encodeURIComponent(`/customers/view/${id}?tab=proposals`)
                                      navigate(`/proposals/view/${proposal.id}?returnTo=${returnTo}`)
                                    }}
                                    className="text-blue-600 hover:text-blue-800 text-sm"
                                  >
                                    View
                                  </button>
                                  <button
                                    onClick={() => {
                                      const returnTo = encodeURIComponent(`/customers/view/${id}?tab=proposals`)
                                      navigate(`/proposals/edit/${proposal.id}?returnTo=${returnTo}`)
                                    }}
                                    className="text-gray-600 hover:text-gray-800 text-sm"
                                  >
                                    Edit
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    </>
                  )}
                </div>

                {/* Proposal Requests Section */}
                <div className="bg-indigo-50 rounded-lg p-6">
                  <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
                    <div className="flex items-center gap-2">
                      <ClipboardListIcon className="w-6 h-6 text-indigo-600" />
                      <h2 className="text-xl font-semibold text-gray-900">
                        Proposal Requests ({proposalRequests.length})
                      </h2>
                    </div>
                    <button
                      onClick={() => {
                        const returnTo = encodeURIComponent(`/customers/view/${id}?tab=proposals`)
                        navigate(`/proposal-requests/create?customerId=${id}&returnTo=${returnTo}`)
                      }}
                      className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                    >
                      <PlusIcon className="w-4 h-4" />
                      New Request
                    </button>
                  </div>

                  {proposalRequests.length === 0 ? (
                    <div className="bg-white rounded-lg border border-dashed border-indigo-200 p-8 text-center">
                      <ClipboardListIcon className="w-10 h-10 text-indigo-300 mx-auto mb-3" />
                      <p className="text-gray-500">No proposal requests found</p>
                    </div>
                  ) : (
                    <>
                    <ul className="space-y-2 lg:hidden">
                      {proposalRequests.map((request) => (
                        <li key={request.id} className="rounded-xl border border-indigo-100 bg-white p-3 space-y-1.5">
                          <button
                            type="button"
                            onClick={() => {
                              const returnTo = encodeURIComponent(`/customers/view/${id}?tab=proposals`)
                              navigate(`/proposal-requests/view/${request.id}?returnTo=${returnTo}`)
                            }}
                            className="text-sm font-semibold text-blue-600 text-left"
                          >
                            {request.title || 'Untitled Request'}
                          </button>
                          <p className="text-xs text-gray-500">{request.request_number || request.proposal_number}</p>
                          <p className="text-sm text-gray-900">{request.status || 'submitted'}</p>
                        </li>
                      ))}
                    </ul>
                    <div className="hidden lg:block overflow-x-auto bg-white rounded-lg border border-indigo-100 shadow-sm">
                      <table className="w-full">
                        <thead className="bg-indigo-50/80">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Title
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Property
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Amount
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Status
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Date
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {proposalRequests.map((request) => (
                            <tr key={request.id} className="hover:bg-indigo-50/30">
                              <td className="px-4 py-3">
                                <button
                                  onClick={() => {
                                    const returnTo = encodeURIComponent(`/customers/view/${id}?tab=proposals`)
                                    navigate(`/proposal-requests/view/${request.id}?returnTo=${returnTo}`)
                                  }}
                                  className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                                >
                                  {request.title || 'Untitled Request'}
                                </button>
                                <div className="text-xs text-gray-500">{request.request_number || request.proposal_number}</div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {request.property?.name || request.property?.address || request.property_address || 'N/A'}
                              </td>
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                {request.estimated_budget ? `$${request.estimated_budget.toLocaleString()}` : 'N/A'}
                              </td>
                              <td className="px-4 py-3">
                                <span
                                  className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                    request.status === 'converted'
                                      ? 'bg-green-100 text-green-800'
                                      : request.status === 'submitted'
                                      ? 'bg-blue-100 text-blue-800'
                                      : request.status === 'reviewing' || request.status === 'in_progress'
                                      ? 'bg-yellow-100 text-yellow-800'
                                      : request.status === 'declined'
                                      ? 'bg-red-100 text-red-800'
                                      : 'bg-gray-100 text-gray-800'
                                  }`}
                                >
                                  {request.status?.charAt(0).toUpperCase() + request.status?.slice(1) || 'Submitted'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {new Date(request.created_at).toLocaleDateString()}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => {
                                      const returnTo = encodeURIComponent(`/customers/view/${id}?tab=proposals`)
                                      navigate(`/proposal-requests/view/${request.id}?returnTo=${returnTo}`)
                                    }}
                                    className="text-blue-600 hover:text-blue-800 text-sm"
                                  >
                                    View
                                  </button>
                                  <button
                                    onClick={() => {
                                      const returnTo = encodeURIComponent(`/customers/view/${id}?tab=proposals`)
                                      navigate(`/proposal-requests/edit/${request.id}?returnTo=${returnTo}`)
                                    }}
                                    className="text-gray-600 hover:text-gray-800 text-sm"
                                  >
                                    Edit
                                  </button>
                                  {request.status !== 'converted' && (
                                    <button
                                      onClick={() => {
                                        const returnTo = encodeURIComponent(`/customers/view/${id}?tab=proposals`)
                                        navigate(`/proposals/create?requestId=${request.id}&returnTo=${returnTo}`)
                                      }}
                                      className="text-green-600 hover:text-green-800 text-sm"
                                      title="Create Proposal"
                                    >
                                      Create Proposal
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Invoices Tab */}
            {activeTab === 'invoices' && (
              <div className="space-y-6">
                <div className="bg-cyan-50 rounded-lg p-6">
                  <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
                    <div className="flex items-center gap-2">
                      <CreditCardIcon className="w-6 h-6 text-cyan-600" />
                      <h2 className="text-xl font-semibold text-gray-900">
                        Invoices ({invoices.length})
                      </h2>
                    </div>
                    <div className="flex items-center gap-2">
                    <button
                      onClick={handleDownloadStatement}
                      disabled={statementBusy !== null}
                      className="flex items-center gap-2 px-3 py-2 border border-gray-300 bg-white text-gray-700 rounded-lg hover:bg-gray-50 text-sm disabled:opacity-50"
                    >
                      <FileTextIcon className="w-4 h-4" />
                      {statementBusy === 'download' ? 'Generating…' : 'Download Statement'}
                    </button>
                    <button
                      onClick={handleEmailStatement}
                      disabled={statementBusy !== null}
                      className="flex items-center gap-2 px-3 py-2 border border-gray-300 bg-white text-gray-700 rounded-lg hover:bg-gray-50 text-sm disabled:opacity-50"
                    >
                      <CreditCardIcon className="w-4 h-4" />
                      {statementBusy === 'email' ? 'Sending…' : 'Email Statement'}
                    </button>
                    <button
                    onClick={() => navigate(`/invoice-generation?customer=${id}`)}
                    className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                  >
                    <PlusIcon className="w-4 h-4" />
                    Create Invoice
                  </button>
                  </div>
                </div>

                {invoices.length === 0 ? (
                  <div className="bg-white rounded-lg border border-dashed border-cyan-200 p-8 text-center">
                    <CreditCardIcon className="w-10 h-10 text-cyan-300 mx-auto mb-3" />
                    <p className="text-gray-500">No invoices found</p>
                  </div>
                ) : (
                  <>
                  <ul className="space-y-2 lg:hidden">
                    {invoices.map((invoice) => {
                      const total = Number(invoice.total || invoice.total_amount || invoice.subtotal || 0)
                      const amountPaid = Number(invoice.amount_paid ?? 0)
                      const balanceDue = Number(invoice.balance_due ?? (total - amountPaid))
                      const computedStatus = getInvoiceDisplayStatus({ status: invoice.status, balance_due: balanceDue, due_date: invoice.due_date })
                      return (
                        <li key={invoice.id} className="rounded-xl border border-cyan-100 bg-white p-3 space-y-1.5">
                          <button
                            type="button"
                            onClick={() => navigate(`/invoice-generation?invoice=${invoice.id}`)}
                            className="text-sm font-semibold text-blue-600 text-left"
                          >
                            {invoice.invoice_number || 'N/A'}
                          </button>
                          <p className="text-sm text-gray-900">
                            ${total.toLocaleString('en-US', { minimumFractionDigits: 2 })} · {getInvoiceStatusLabel(computedStatus)}
                          </p>
                          {balanceDue > 0 && (
                            <button
                              type="button"
                              onClick={() => navigate(`/payments?invoiceId=${invoice.id}`)}
                              className="text-sm text-green-600 font-medium"
                            >
                              Record Payment
                            </button>
                          )}
                        </li>
                      )
                    })}
                  </ul>
                  <div className="hidden lg:block overflow-x-auto bg-white rounded-lg border border-cyan-100 shadow-sm">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-cyan-50/80">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Invoice #
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Amount
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Issue Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Due Date
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {invoices.map((invoice) => {
                          const total = Number(invoice.total || invoice.total_amount || invoice.subtotal || 0)
                          const amountPaid = Number(invoice.amount_paid ?? 0)
                          const balanceDue = Number(invoice.balance_due ?? (total - amountPaid))
                          const computedStatus = getInvoiceDisplayStatus({ status: invoice.status, balance_due: balanceDue, due_date: invoice.due_date })
                          const hasBalance = balanceDue > 0
                          return (
                            <tr key={invoice.id} className="hover:bg-cyan-50/30">
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {invoice.invoice_number || 'N/A'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                ${total.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span
                                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getInvoiceStatusColor(computedStatus)}`}
                                >
                                  {getInvoiceStatusLabel(computedStatus)}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {(invoice.invoice_date || invoice.issue_date)
                                  ? new Date(invoice.invoice_date || invoice.issue_date).toLocaleDateString()
                                  : 'N/A'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {invoice.due_date
                                  ? new Date(invoice.due_date).toLocaleDateString()
                                  : 'N/A'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right">
                                {hasBalance && (
                                  <button
                                    onClick={() => navigate(`/payments?invoiceId=${invoice.id}`)}
                                    className="text-green-600 hover:text-green-900 mr-4 inline-flex items-center gap-1"
                                    title="Record Payment"
                                  >
                                    <CreditCardIcon className="w-4 h-4" />
                                    Record Payment
                                  </button>
                                )}
                                <button
                                  onClick={() => navigate(`/invoice-generation?invoice=${invoice.id}`)}
                                  className="text-blue-600 hover:text-blue-900 mr-4"
                                >
                                  View
                                </button>
                                <button
                                  onClick={() => navigate(`/billing`)}
                                  className="text-gray-600 hover:text-gray-900"
                                >
                                  Billing
                                </button>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                  </>
                )}
                </div>
              </div>
            )}

            {activeTab === 'notes' && (
              <NotesAndActivityPanel
                notes={[...notes].reverse().map((n) => ({ note: n.note, timestamp: n.timestamp }))}
                activities={activities}
                notesTitle="Customer Notes"
                newNote={newNote}
                onNewNoteChange={setNewNote}
                onAddNote={handleAddNote}
                savingNote={savingNote}
              />
            )}
          </div>
      </div>

      <div className="native-action-bar flex items-center justify-between gap-3 max-w-6xl mx-auto lg:mt-4 lg:rounded-xl lg:border lg:border-gray-200">
        <button type="button" onClick={handleBack} className={mobile.secondaryBtn}>
          {backLabel}
        </button>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setActionsOpen(true)} className={mobile.secondaryBtn}>
            <MoreHorizontalIcon className="w-4 h-4" />
            Actions
          </button>
          <button
            type="button"
            onClick={() => navigate(`/customers/edit/${id}`)}
            className={mobile.primaryBtn}
          >
            <EditIcon className="w-4 h-4" />
            Edit Customer
          </button>
        </div>
      </div>

      <MobileSheet open={actionsOpen} onClose={() => setActionsOpen(false)} title="Customer actions">
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => {
              setActionsOpen(false)
              const rt = encodeURIComponent(`/customers/view/${id}?tab=contracts`)
              navigate(`/contracts/create?customerId=${id}&returnTo=${rt}`)
            }}
            className={`${mobile.secondaryBtn} w-full`}
          >
            <ClipboardIcon className="w-4 h-4" />
            New Contract
          </button>
          <button
            type="button"
            onClick={() => {
              setActionsOpen(false)
              const rt = encodeURIComponent(`/customers/view/${id}?tab=jobs`)
              navigate(`/jobs/create?customer_id=${id}&returnTo=${rt}`)
            }}
            className={`${mobile.secondaryBtn} w-full`}
          >
            <BriefcaseIcon className="w-4 h-4" />
            New Job
          </button>
          <button
            type="button"
            onClick={() => {
              setActionsOpen(false)
              handleDownloadStatement()
            }}
            disabled={statementBusy !== null}
            className={`${mobile.secondaryBtn} w-full`}
          >
            <FileTextIcon className="w-4 h-4" />
            {statementBusy === 'download' ? 'Generating…' : 'Download Statement'}
          </button>
          <button
            type="button"
            onClick={() => {
              setActionsOpen(false)
              handleEmailStatement()
            }}
            disabled={statementBusy !== null}
            className={`${mobile.secondaryBtn} w-full`}
          >
            <CreditCardIcon className="w-4 h-4" />
            {statementBusy === 'email' ? 'Sending…' : 'Email Statement'}
          </button>
          <button
            type="button"
            onClick={() => {
              setActionsOpen(false)
              navigate(`/customers/edit/${id}`)
            }}
            className={`${mobile.primaryBtn} w-full`}
          >
            <EditIcon className="w-4 h-4" />
            Edit Customer
          </button>
        </div>
      </MobileSheet>
    </MobilePage>
  )
}
